<script setup>
/**
 * The teammate cell as a control: a searchable picker over the agents that can
 * take this conversation's inbox. Writes through `assignAgent`, the action the
 * sidebar's assignee picker uses, so an inline assignment behaves exactly like
 * one made from the thread.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import {
  getAgentsByUpdatedPresence,
  getSortedAgentsByAvailability,
} from 'dashboard/helper/agentHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import CellMenu from './CellMenu.vue';
import CellMenuItem from './CellMenuItem.vue';

const props = defineProps({
  conversationId: { type: Number, required: true },
  inboxId: { type: Number, default: null },
  assignee: { type: Object, default: null },
});

const { t } = useI18n();
const store = useStore();

const searchQuery = ref('');

const currentUser = useMapGetter('getCurrentUser');
const currentAccountId = useMapGetter('getCurrentAccountId');
const assignableAgents = useMapGetter(
  'inboxAssignableAgents/getAssignableAgents'
);

// Same ordering the conversation context menu uses: the live presence of the
// current user first, then agents sorted by availability.
const agents = computed(() => {
  const records = assignableAgents.value(props.inboxId);
  return getSortedAgentsByAvailability(
    getAgentsByUpdatedPresence(
      records,
      currentUser.value,
      currentAccountId.value
    )
  );
});

const filteredAgents = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return agents.value;
  return agents.value.filter(agent =>
    `${agent.name} ${agent.email}`.toLowerCase().includes(query)
  );
});

const agentLabel = agent =>
  agent.id === currentUser.value?.id
    ? `${agent.name} ${t('TICKETS.TABLE.CELL.YOU')}`
    : agent.name;

const fetchAgents = () => {
  searchQuery.value = '';
  if (props.inboxId)
    store.dispatch('inboxAssignableAgents/fetch', [props.inboxId]);
};

const assign = async (agent, hide) => {
  hide();
  if ((agent?.id ?? null) === (props.assignee?.id ?? null)) return;
  try {
    await store.dispatch('assignAgent', {
      conversationId: props.conversationId,
      agentId: agent ? agent.id : null,
      assigneeType: agent ? agent.assignee_type || 'User' : null,
    });
  } catch (error) {
    useAlert(t('TICKETS.TABLE.CELL.ERROR'));
  }
};
</script>

<template>
  <CellMenu width="w-72" @show="fetchAgents">
    <template #trigger="{ isOpen }">
      <button
        type="button"
        data-testid="assignee-trigger"
        aria-haspopup="menu"
        :aria-expanded="isOpen"
        :aria-label="t('TICKETS.TABLE.CELL.CHANGE_ASSIGNEE')"
        class="inline-flex items-center max-w-full gap-2 px-1.5 py-1 rounded-md hover:bg-n-alpha-2"
        :class="isOpen ? 'bg-n-alpha-2' : ''"
      >
        <template v-if="assignee">
          <Avatar
            :name="assignee.name"
            :src="assignee.thumbnail"
            :size="24"
            rounded-full
          />
          <span class="text-sm truncate text-n-slate-11">
            {{ assignee.name }}
          </span>
        </template>
        <span v-else class="text-sm text-n-slate-10">
          {{ t('TICKETS.INFO.UNASSIGNED') }}
        </span>
        <span
          class="i-lucide-chevron-down size-3 shrink-0 text-n-slate-10 opacity-0 group-hover/row:opacity-100"
          :class="isOpen ? 'opacity-100' : ''"
        />
      </button>
    </template>
    <template #default="{ hide }">
      <Input
        v-model="searchQuery"
        type="search"
        size="sm"
        autofocus
        class="mb-1"
        :placeholder="t('TICKETS.TABLE.CELL.SEARCH_TEAMMATES')"
      />
      <div class="flex flex-col overflow-y-auto max-h-64">
        <CellMenuItem
          role="menuitemradio"
          data-testid="assignee-option-none"
          icon="i-lucide-user-round-x"
          :label="t('TICKETS.INFO.UNASSIGNED')"
          :selected="!assignee"
          @click="assign(null, hide)"
        />
        <CellMenuItem
          v-for="agent in filteredAgents"
          :key="agent.id"
          role="menuitemradio"
          :data-testid="`assignee-option-${agent.id}`"
          :label="agentLabel(agent)"
          :selected="agent.id === assignee?.id"
          @click="assign(agent, hide)"
        >
          <template #prefix>
            <Avatar
              :name="agent.name"
              :src="agent.thumbnail"
              :size="20"
              rounded-full
            />
          </template>
          <template #suffix>
            <span class="text-xs truncate ms-auto text-n-slate-10">
              {{ agent.email }}
            </span>
          </template>
        </CellMenuItem>
        <p
          v-if="!filteredAgents.length"
          class="px-2.5 py-2 m-0 text-sm text-n-slate-10"
        >
          {{ t('TICKETS.TABLE.CELL.NO_TEAMMATES') }}
        </p>
      </div>
    </template>
  </CellMenu>
</template>
